import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useProviders } from "@/hooks/useProviders";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, MapPin, ArrowRight } from "lucide-react";
import ProviderImagesFromFacilities from "@/components/ProviderImagesFromFacilities";
import { useProviderMinPrice } from "@/hooks/useProviderMinPrice";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";
import { useDomainProvider } from "@/hooks/useDomainProvider";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const ProviderCard = ({ provider }: { provider: any }) => {
  const navigate = useNavigate();
  const { data: minPrice } = useProviderMinPrice(provider.id);

  const getProviderFallbackImages = (): string[] => {
    if (Array.isArray(provider.images) && provider.images.length > 0) {
      return provider.images;
    } else if (provider.image_url) {
      return [provider.image_url];
    }
    return [];
  };

  return (
    <ProviderImagesFromFacilities providerId={provider.id} fallbackImages={getProviderFallbackImages()}>
      {(images) => (
        <div className="group cursor-pointer" onClick={() => navigate(`/provider/${provider.slug || provider.id}`)}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl mb-3">
            <img
              src={images[0] || "/placeholder.svg"}
              alt={provider.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-foreground group-hover:text-foreground/70 transition-colors">
                {provider.name}
              </h3>
              <div className="flex items-center gap-1 text-sm shrink-0">
                <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
                <span className="text-sm">{provider.rating || "5"}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 text-muted-foreground text-sm">
              <MapPin className="h-3 w-3" />
              <span>{provider.location}</span>
            </div>

            <div className="text-sm">
              <span className="font-medium">{minPrice ? `From £${Number(minPrice).toFixed(0)}` : "From £25"}</span>
            </div>
          </div>
        </div>
      )}
    </ProviderImagesFromFacilities>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: providers, isLoading } = useProviders();
  const { loading, setLoading, handleLogin, handleSignup, handleForgotPassword } = useAuthHandlers();
  const { isDomainLocked, lockedProvider, isLoading: domainLoading } = useDomainProvider();

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  
  // Filter providers when domain is locked
  const displayProviders = isDomainLocked && lockedProvider 
    ? providers?.filter(p => p.id === lockedProvider.id) 
    : providers;

  // Redirect to locked provider when on custom domain (replaces hardcoded my.cold-culture.co.uk check)
  useEffect(() => {
    if (domainLoading) return;
    
    const isRootPath = window.location.pathname === "/";

    if (isDomainLocked && lockedProvider && isRootPath && !user) {
      navigate(`/provider/${lockedProvider.slug}`, { replace: true });
    }
  }, [navigate, user, isDomainLocked, lockedProvider, domainLoading]);

  // 1) Keep your existing mobile behaviour first
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("mobile") === "true") {
      navigate("/auth");
    }
  }, [navigate]);

  // 3) Your existing redirect for authenticated users
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await handleLogin(email, password);
        toast.success("Welcome back!");
      } else {
        const favoriteProviderId = localStorage.getItem("pendingFavoriteProvider");
        const message = await handleSignup(email, password, firstName, lastName, favoriteProviderId);
        toast.success(message);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = async () => {
    if (!email) {
      toast.error("Please enter your email address first.");
      return;
    }
    setLoading(true);
    try {
      const message = await handleForgotPassword(email);
      toast.success(message);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Wellsuites — Book wellness & recovery sessions near you"
        description="Discover and book sauna, cold plunge, and recovery sessions at top wellness studios. Real-time availability, instant booking, seamless check-in."
        canonical="https://wellsuites.co/"
      />
      {/* Mobile: Show providers above form */}
      <div className="lg:hidden px-6 pt-8 pb-6">
        {(!isDomainLocked || (displayProviders && displayProviders.length > 0)) && (
          <>
            <div className="mb-6">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {isDomainLocked ? 'Welcome to' : 'Available venues'}
              </p>
              <h2 className="text-xl font-semibold text-foreground">Book your wellness session</h2>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 gap-6">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/3] bg-muted rounded-xl mb-3"></div>
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {displayProviders?.map((provider) => (
                  <ProviderCard key={provider.id} provider={provider} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 lg:min-h-screen">
        {/* Left - Auth Form */}
        <div className="flex flex-col justify-center px-6 py-8 lg:py-12 lg:px-12 xl:px-20">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
                {isLogin ? "Welcome back" : "Create account"}
              </h1>
              <p className="text-muted-foreground">
                {isLogin ? "Sign in to book your wellness session" : "Join to start booking wellness sessions"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm">
                      First name
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="h-11"
                      required={!isLogin}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm">
                      Last name
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="h-11"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  required
                />
              </div>

              <Button type="submit" className="w-full h-11 font-medium" disabled={loading}>
                {loading ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
                {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </form>

            {isLogin && (
              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
              >
                Forgot password?
              </button>
            )}

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                {isLogin ? "New here? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-foreground font-medium hover:underline"
                >
                  {isLogin ? "Create an account" : "Sign in"}
                </button>
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground">
                By continuing, you agree to our{" "}
                <Link to="/terms" className="underline hover:text-foreground">
                  Terms
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline hover:text-foreground">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right - Provider Cards (Desktop only) */}
        <div className="hidden lg:block bg-muted/30 border-l border-border overflow-y-auto">
          <div className="p-8 xl:p-12">
            {(!isDomainLocked || (displayProviders && displayProviders.length > 0)) && (
              <>
                <div className="mb-6">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {isDomainLocked ? 'Welcome to' : 'Available venues'}
                  </p>
                  <h2 className="text-xl font-semibold text-foreground">Book your wellness session</h2>
                </div>

                {isLoading ? (
                  <div className="grid gap-6">
                    {[1, 2].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="aspect-[4/3] bg-muted rounded-xl mb-3"></div>
                        <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {displayProviders?.map((provider) => (
                      <ProviderCard key={provider.id} provider={provider} />
                    ))}
                  </div>
                )}

                {!isLoading && (!displayProviders || displayProviders.length === 0) && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No venues available.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
